'use client';
import { CartHorizontalScroll } from './CartHorizontalScroll';

import { type ElementType, type ReactNode } from 'react';
import Cookies from 'js-cookie';
import { Link, LucideIcon } from '@skin-seoul/design-system';
import {
  createReviewStatsMap,
  RECENTLY_VIEWED_PRODUCTS_KEY,
  useProductService,
  useReviewService,
  useWishlistOwner,
  useWishlistService,
  type Currency,
  type CustomerKeyType,
  type Product,
  type VariationProduct,
  orderProductsByKeys,
  parseRecentlyViewedProductIds,
} from '@skin-seoul/service';
import QueryBoundary from '../../../utils/react/QueryBoundary';
import { CartDiscoveryProductCard } from './CartDiscoveryProductCard';
import { CartSavedProductsSkeleton } from './CartSavedProducts.Skeleton';
import { CartSectionAnalytics, useCartAnalytics } from './CartAnalytics';

interface DiscoveryProps {
  onCartMutation?: (mutation: Promise<boolean>) => void;
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
}

function SavedProductList({
  products,
  title,
  headerAction,
  ...props
}: DiscoveryProps & { products: Array<Product | VariationProduct>; title: string; headerAction?: ReactNode }) {
  const placement = title === 'My Wishlist' ? 'cart_wishlist' : 'cart_recently_viewed';
  const { useGetReviewStatistics } = useReviewService();
  const { data } = useGetReviewStatistics({ productIds: products.map(({ productId }) => productId).join(',') });
  const stats = createReviewStatsMap(data);
  if (!products.length) {
    return null;
  }
  return (
    <CartSectionAnalytics placement={placement} count={products.length}>
      <section aria-label={title} className="border-t-8 border-neutral-50 px-4 py-5">
        <div className="mb-6 flex items-center justify-between gap-2">
          <h2 className="text-paragraph-medium font-medium text-generic-100">{title}</h2>
          {headerAction}
        </div>
        <CartHorizontalScroll className="-mx-4 px-4 flex gap-2.5 overflow-x-auto snap-x snap-proximity scroll-px-4 pb-2 scrollbar-none">
          {products.map((product, index) => (
            <CartDiscoveryProductCard
              key={product.sku}
              product={product}
              rating={stats[product.productId]}
              {...props}
              attribution={{ placement, position: index }}
            />
          ))}
        </CartHorizontalScroll>
      </section>
    </CartSectionAnalytics>
  );
}

function WishlistProducts({ skus, ...props }: DiscoveryProps & { skus: string[] }) {
  const { track } = useCartAnalytics();
  const { useSuspenseProductsBySkus } = useProductService();
  const { data } = useSuspenseProductsBySkus({ skus: skus.join(','), currency: props.currency });
  const products = orderProductsByKeys(data, skus, (product) => product.sku);
  const LinkComponent = props.linkComponent || Link;
  return (
    <SavedProductList
      {...props}
      title="My Wishlist"
      products={products}
      headerAction={
        <LinkComponent
          href="/my-account/wishlist/"
          onClick={() => {
            track('section_click', { section_name: 'cart_wishlist', page: 'cart', action: 'view_all' });
            props.onProductClick();
          }}
          className="flex items-center gap-1 text-label-medium text-neutral-500 no-underline hover:no-underline"
        >
          view all <LucideIcon name="ChevronRight" size={20} />
        </LinkComponent>
      }
    />
  );
}

function WishlistData({
  customerKey,
  customerKeyType,
  ...props
}: DiscoveryProps & { customerKey: string; customerKeyType: CustomerKeyType }) {
  const { useSuspenseWishlist } = useWishlistService();
  const { data } = useSuspenseWishlist({ customerKey, customerKeyType });
  const skus = [...new Set(data?.skus ?? [])];
  if (!skus.length) {
    return null;
  }
  return <WishlistProducts skus={skus} {...props} />;
}

export function CartWishlist(props: DiscoveryProps) {
  const { customerKey, customerKeyType, isOwnerResolved } = useWishlistOwner();
  if (!isOwnerResolved) {
    return <CartSavedProductsSkeleton />;
  }
  if (!customerKey) {
    return null;
  }
  return (
    <QueryBoundary
      key={`${customerKeyType}:${customerKey}:${props.currency}`}
      clientOnly
      loadingFallback={<CartSavedProductsSkeleton />}
      errorFallback={<></>}
    >
      <WishlistData customerKey={customerKey} customerKeyType={customerKeyType} {...props} />
    </QueryBoundary>
  );
}

function RecentlyViewedProducts({ ids, ...props }: DiscoveryProps & { ids: string[] }) {
  const { useGetSuspenseProductsByIds } = useProductService();
  const { data } = useGetSuspenseProductsByIds({ ids: ids.join(','), currency: props.currency });
  const products = orderProductsByKeys(data, ids, (product) => String(product.productId));
  return <SavedProductList {...props} title="Recently Viewed" products={products} />;
}

export function CartRecentlyViewed(props: DiscoveryProps) {
  const ids = parseRecentlyViewedProductIds(Cookies.get(RECENTLY_VIEWED_PRODUCTS_KEY));
  if (!ids.length) {
    return null;
  }
  return (
    <QueryBoundary
      key={`${props.currency}:${ids.join(',')}`}
      clientOnly
      loadingFallback={<CartSavedProductsSkeleton />}
      errorFallback={<></>}
    >
      <RecentlyViewedProducts ids={ids} {...props} />
    </QueryBoundary>
  );
}
